import { Button, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LocationsContext, newLocation } from '../context/locations';
import { Bounds, FullLocationsResponse, MapLocation } from '../core/global';
import { SavedLocationsModal } from './saved-locations-modal';
// import { locationSearch } from '../api/maps';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { toggleLocation } from '../api/locations';
import { MapDataContext } from '../context/map-data';
import styles from '../styles/components/locations-list.module.scss';
import { InfoMessage } from './error-message';
import { AreaInfoPanel } from './info-panel';

interface ListProps {
  data: FullLocationsResponse[]
  setCurrentLocation: (uid: number | null) => void
  onUpdateVisibility: () => void
}

export const List = ({
  data,
  setCurrentLocation,
  onUpdateVisibility,
}: ListProps) => {
  
  const {
    setMapCenter,
  } = useContext(MapDataContext);

  const toggle = useCallback(async (uids: number[], visible: boolean, refetch = true) => {
    for (let index = 0; index < uids.length; index++) {
      const uid = uids[index];
      await toggleLocation({
        uid,
        visible
      })
    }
    if (refetch) {
      onUpdateVisibility()
    }
  }, [])
  
  const currentSelectedArea = useMemo(() => {
    const allVisible = (data || []).filter( item => item.visible)
    const noneVisible =  allVisible.length === 0
    let currentSelectedArea: any = (noneVisible) ? 'none' : null
    
    const found = (data || []).find( item => item.visible)
    if (found) {
      const val = found?.uid || null
      currentSelectedArea = val
    }

    return currentSelectedArea

  }, [data])

  useEffect(() => {
    setCurrentLocation(currentSelectedArea)
  }, [currentSelectedArea])


  const changeSelectedUid = useCallback(({
    centroid,
    uid,
    bounds
  }: any) => {
    const changeStatusToHidden = data.filter( item => item.visible).map( item => item.uid)
    toggle(changeStatusToHidden, false, false)
    toggle([uid], true)
    if (centroid) {
      const [lat, lon] = centroid.split(' ')
      let mapCenter: MapLocation = {location: {lat, lng: lon}}
      if (bounds) {
        const converted = convertPolygonToBounds(bounds)
        if (converted) {
          mapCenter = {
            location: {
              lat, lng: lon}, 
            bounds: {...converted}
          }
        }
      }
      setMapCenter(mapCenter)
    }
    setCurrentLocation(uid)
  }, [data])

  return (
    <>
      <Select
        className={styles.select}
        size="small"
        labelId="data-select"
        id="data-select"
        value={`${currentSelectedArea}`}
      >
        <MenuItem
          key={`none`}
          value={'none'} 
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            const changeStatusToHidden = data.filter( item => item.visible).map( item => item.uid)
            toggle(changeStatusToHidden, false)
            setCurrentLocation(null)
          }
        }>Hide all...</MenuItem>
        {data.map(row => {
          const {title, uid, visible, centroid, bounds} = row
            return (
              <MenuItem
                key={`${uid}`} 
                value={uid} 
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  changeSelectedUid({
                    centroid,
                    uid,
                    bounds
                  })
                }
              }>{title}</MenuItem>
            )
          })
        }
      </Select>
    </>
  )
}

export const convertPolygonToBounds = (polygon: string | null): Bounds | null => {
  
  if (!polygon || typeof polygon !== 'string' || polygon.toLowerCase().indexOf('polygon') === -1 ) return null;

  try {
    const parsed = JSON.parse(polygon)
    if (parsed?.coordinates && Array.isArray(parsed.coordinates) && parsed.coordinates.length > 0 && Array.isArray(parsed.coordinates[0])) {
      let minLat = Number.POSITIVE_INFINITY;
      let minLon = Number.POSITIVE_INFINITY;
      let maxLat = Number.NEGATIVE_INFINITY;
      let maxLon = Number.NEGATIVE_INFINITY;
    
      parsed.coordinates[0].forEach(([lon, lat]) => {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      });
    
      return { 
        min: {
          lat: minLat, 
          lng: minLon
        },
        max: {
          lat: maxLat, 
          lng: maxLon
        },};
    }
  } catch {
  }
  return null
  // const coordinates = polygon
  //   .replace(/polygon|\(|\)/g, '')
  //   .trim()
  //   .split(',')
  //   .map(coord => coord.trim().split(' ').map(Number));

};

export const LocationsList = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [currentLocationUid, setCurrentLocationUid] = useState<number | string | null>(null)
  
  const { locations,
    refetchLocations, 
    setCurrentLocation, 
    currentLocation, 
    refetchLocationShapes,
    setNotSavedYetLocationShapes,
    hasLocations,
  } = useContext(LocationsContext);

  const handleCreateLocation = useCallback(() => {
    setCurrentLocation(newLocation)
    refetchLocationShapes(null)
  }, [])

  useEffect(() => {
    if (currentLocation !== null) {
      setModalOpen(true)
    } else {
      setModalOpen(false)
    }
  }, [currentLocation])

  useEffect(() => {
    if (locations === null) {
      refetchLocations()
    }
  }, [locations])

  return (
    <>
      {(hasLocations || locations !== null) ? 
        <>
          <div className={styles.row}>
          <List
            data={locations || []} 
            onUpdateVisibility={() => {
                refetchLocations()
              }
            } 
            setCurrentLocation={setCurrentLocationUid} 
          />
          <Tooltip
            title={'Create new'} 
            placement="bottom"
            arrow
          >
            <span>
              <IconButton
                className={styles.button}
                onClick={handleCreateLocation}
              >
                <AddIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={'Edit'} 
            placement="bottom" 
            arrow
          >
            <span>
              <IconButton
                className={styles.button}
                // variant="contained"
                disabled={currentLocationUid === null || currentLocationUid === 'none'}
                onClick={() => {
                  setCurrentLocation(currentLocationUid)
                }}
                sx={{
                  marginLeft: '8px'
                }}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          </div>
          <AreaInfoPanel currentLocationUid={currentLocationUid} />  
          {/* <Button >Create new</Button> */}
        </>
        :
        <>
          <InfoMessage message={`You can upload **shapes**, **points**, **create** and **explore** regions of interest.`} />
          <Button 
            onClick={handleCreateLocation} 
            className={styles.button}
            variant="outlined"
            startIcon={<AddIcon sx={{color: '#010824'}} />}
          >Create your first location</Button>
        </>
      }
      {modalOpen && <SavedLocationsModal 
        open={modalOpen} 
        handleClose={(refetch: boolean) => {
          setCurrentLocation(null)
          if (refetch) {
            refetchLocations()
          }
          setNotSavedYetLocationShapes(null)
          // setCurrentLocationUid(null)
        }} 
        data={currentLocation} 
      />}
    </>
  )
}